import React, { useEffect, useRef, useState } from 'react';
import HighchartsAnnotations from 'highcharts/modules/annotations';
import HighchartsReact from 'highcharts-react-official';
import HighStock from "highcharts/highstock";
import Tags from './Tags';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { purple, sc2Timestamps, sc3Timestamps, sc4Timestamps, unitMap } from '../utils';
import PredefinedButtons from './PredefinedButtons';
import Timecontroler from './Timecontroler';
import InfosStation from './InfosStation';
import { MdCopyright } from 'react-icons/md';
import { Spin } from 'antd';
 
HighchartsAnnotations(HighStock);

dayjs.locale('fr');

const updatePlotLine = (chartOptions, position, color) => {
  const updatedPlotLines = [...chartOptions?.xAxis.plotLines];

  const existingIndex = updatedPlotLines.findIndex(plotLine => plotLine.color === color);

  if (existingIndex !== -1 && color === purple) {
    updatedPlotLines[existingIndex] = {
      ...updatedPlotLines[existingIndex],
      value: position,
      color,
      width: 1,
      zIndex: 5,
    };
  } else {
    updatedPlotLines.push({
      value: position,
      color,
      width: 1,
      zIndex: 5,
    });
  }

  return {
    ...chartOptions,
    xAxis: {
      ...chartOptions.xAxis,
      plotLines: updatedPlotLines,
    },
  };
};
const timeIntervals = [1, 6, 12, 24, 48];

const fetchPredictionsJSON = async (linePosition, setChartOptions, lineName, color, forecastData) => {
  const linePositionDate = Math.floor(linePosition / 1000);
  try {
    const timestampKey = Object.keys(forecastData).find(
      (key) => parseInt(key) === linePositionDate
    ); 
    if (timestampKey) {
      const forecasts = forecastData[timestampKey].forecasts;
      const forecastSeries = {
        timestamp: timestampKey * 1000,
        type: 'line',
        name: lineName,
        lineWidth: lineName.startsWith('T0') ? 2.5 : 1.8,
        data: forecasts.map((value, index) => {
          const forecastValue = Number(value.toFixed(2));
          const timestamp0 = parseInt(timestampKey) * 1000;
          const incrementTimestamp = (parseInt(timestampKey) + index * 3600) * 1000;

          return [
            index === 0 ? timestamp0 : incrementTimestamp,
            forecastValue
          ];
        }),
        color: color || purple,
        yAxis: 0,
        states: {
          hover: {
            lineWidth: 1,
          },
        },
      };

      setChartOptions((prevOptions) => {
        const otherLinesPresent = prevOptions.series?.some(series => series.name.startsWith("T-"));
        const seriesExists = prevOptions.series?.some(series => series.name === forecastSeries.name);
        const isT0LineName = lineName.startsWith('T0');

        if (isT0LineName) {
          if (!otherLinesPresent) {
            const updatedSeries = prevOptions.series?.filter(
              series => series.lineWidth !== 2.5
            );
            return {
              ...prevOptions,
              series: [...updatedSeries, forecastSeries],
            };
          }
          return prevOptions;
        }
 
        return {
          ...prevOptions,
          series: seriesExists ? prevOptions.series : [...prevOptions.series, forecastSeries],
        };
      });
    }
  } catch (error) {
    console.error('Erreur lors du chargement du JSON:', error);
  }
};

 
const configureChartOptions = (
  seriesData,
  sortedXValues,
  setDraggableLinePosition,
  hasForecast,
  forecastData,
  isCompareStation,
) => {
  const initialXMin = sortedXValues[0];
  const initialXMax = sortedXValues[192];
  const stationName = seriesData[0]?.name;
  const stationData = seriesData[0]?.data;

  let array = [];
  stationData?.map(item => array.push(item[1]))

  const initialYMin = Math.max(Math.min(...array) - ( Math.max(...array) - Math.min(...array) ) * 1, 0)
  const initialYMax = Math.max(...array) + (Math.max(...array) - Math.min(...array)) * 0.5;
  const firstForecastTimestamp = Math.min(...Object.keys(forecastData).map(Number));
  const lastForecastTimestamp = Math.max(...Object.keys(forecastData).map(Number));

  const unitKey = stationName?.split('_')[1];
  const unit = unitMap[unitKey] || '';

  return ({
    chart: {
      height: isCompareStation ? 270 : 350,
      zoomType: 'x',
      events: {
        load: function () {
          this?.xAxis[0].setExtremes(initialXMin, initialXMax);
          this.yAxis[0].setExtremes(initialYMin, initialYMax);
        },
        selection: function(event) {
          if (event.resetSelection) {
            this.series.forEach((serie) => {
              serie.update({
                marker: {
                  radius: 1
                }
              });
            });
          } else {
            this.series.forEach((serie) => {
              serie.update({
                marker: {
                  radius: 1.5
                }
              });
            });
          }
        }
      },
  },
  navigator: {
    height: isCompareStation ? 12 : 40,
  },
  rangeSelector: {
    enabled: false,
  },
  plotOptions: {
    series: {
      fillColor: {
        linearGradient: [0, 300, 0, 0],
        stops: [
            [0, HighStock.getOptions().colors[0]],
            [
                1,
                HighStock.color(HighStock.getOptions().colors[0])
                    .setOpacity(0).get('rgba')
            ]
        ]
    }
    },
    area: {
      fillOpacity: 0.3,
    },
  },
    title: {
      text: isCompareStation ? stationName : '',
      style: {
        fontSize: '10px',
    },
    },
    xAxis: {
    type: 'datetime',
    crosshair: true,
    plotLines: [],
    plotBands: [
      {
        color: 'rgba(128, 128, 128, 0.3)',
        from: sortedXValues[0],
        to: firstForecastTimestamp * 1000
      },
      { 
        color: 'rgba(128, 128, 128, 0.3)',
        from: lastForecastTimestamp * 1000,
        to: sortedXValues[sortedXValues.length - 1]
      }
    ],
    events: {
      afterSetExtremes: function (e) {
        const minX = e.min;
        const maxX = e.max; 
        const moy = minX + (maxX - minX) / 2;
        const closestX = sortedXValues.length > 0
          ? sortedXValues.reduce((prev, curr) => 
            Math.abs(curr - moy) < Math.abs(prev - moy) ? curr : prev
          )
          : null;
      
        if (closestX !== null && !hasForecast) {
          setDraggableLinePosition(closestX);
        }
      }
    }
      },      
    yAxis: [
      {
        title: { text: unit },
        opposite: false,
      },
      {
        title: { text: 'mm' },
        reversed: true,
        opposite: true,
        tickInterval: 5,
      },
    ],
    tooltip: {
      shared: true,
      split: false,
      distance: 20,
      style: {
        fontSize: '11px',
      },
      formatter: function () {
        const date = dayjs(this.x).format('DD MM YYYY HH:mm');
        const points = this.points;
       
        let historicalData = points?.filter(point => point.series.name.includes("_"));
        let predictionData = points?.filter(point => !point.series.name.includes("_"));
    
        let tooltipContent = `<i><b>${date}</b></i><br/>`;
    
        if (historicalData.length > 0) {
          tooltipContent += `<span style="font-weight: bold; marginBottom: '10px'">Données historiques</span><br/>`;
          historicalData.forEach(point => {
            const unitKey = stationName?.split('_')[1];
            const unit = unitMap[unitKey] || '';
            tooltipContent += `<span style="color:${point.color}">\u25CF</span> ${point.series.name} : <b>${point.y.toFixed(2)}</b> <b>${unit}</b><br/>`;
          });
        }

        if (predictionData.length > 0) {
          tooltipContent += `<span style="font-weight: bold;">Prédictions</span><br/>`;
          predictionData.forEach(point => {
            const unitKey = stationName?.split('_')[1];
            const unit = unitMap[unitKey] || '';
            tooltipContent += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y.toFixed(2)}</b> <b>${unit}</b><br/>`;
          });
        }
        return tooltipContent;
      }
    },
    series: seriesData,
  })
};
 
const configureSeries = (data) => {
  return Object.entries(data).map(([stationCode, stationData]) => {
    const isP = stationCode.endsWith('P');
 
    const formattedData = stationData.map((item) => {
      return (
        [
          item.timestamp * 1000,
          item.value,
        ]
      )
    });
 
    return {
      type: isP ? 'column' : 'area',
      name: stationCode,
      color: isP ? '#086A87' : ' #3366ff',
      data: formattedData,
      lineWidth: 0.2,
      lineWidthPlus: 1,
      states: {
        hover: {
          lineWidth: 0.7,
        },
      },
      yAxis: isP ? 1 : 0,
    };
  });
};
const initializeChart = (data, setDraggableLinePosition, setChartOptions, hasForecast, forecastData, isCompareStation, showCompareStation) => {
  const seriesData = configureSeries(data);
 
  const newXValues = seriesData.reduce((acc, series) => {
    series.data.forEach((point) => acc.add(point[0]));
    return acc;
  }, new Set());
 
  const sortedXValues = Array.from(newXValues).sort((a, b) => a - b);
  
  const chartOptions = configureChartOptions(seriesData, sortedXValues, setDraggableLinePosition, hasForecast, forecastData, isCompareStation, showCompareStation);

  setChartOptions(chartOptions);
};
 
const StationChart = ({ 
  data, 
  forecastData, 
  setOpen, 
  setSteps, 
  selectedStation, 
  scenarioBase, 
  setShowCompareStation, 
  showCompareStation,
  compareData, 
  loadingMainChart,
  loadingCompareChart,
  compareForecastData,
  setCompareData,
  selectedCompareStation,
  setSelectedCompareStation,
}) => {
  const [compareChartOptions, setCompareChartOptions] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);
  const [draggableLinePosition, setDraggableLinePosition] = useState(null);
  const [linePositions, setLinePositions] = useState([]);
  const [newForecastLineTimestamp, setNewForecastLineTimestamp] = useState(null);
  const [isAddNewForecast, setIsAddNewForecast] = useState(false);
  const chartOptionsForecasts = chartOptions?.series?.filter(series => series.name.startsWith("T-"));
  const [hasForecast, setHasForecast] = useState(false);
  const [moveNavigator, setMoveNavigator] = useState(false);
  const [disabledButtons, setDisabledButtons] = useState({});
  const isCompareStation = Object.keys(compareData).length > 0;

  const ref44 = useRef(null);
  const ref45 = useRef(null);
  const ref54 = useRef(null);

  const selectedScenario = {
    sc2: sc2Timestamps,
    sc3: sc3Timestamps,
    sc4: sc4Timestamps,
  }
  const startDatePrediction = selectedScenario[scenarioBase]?.startDate * 1000;
  const endDatePrediction = selectedScenario[scenarioBase]?.endDate * 1000;

  useEffect(() => {
    const validCharts = HighStock.charts.filter(chart => chart !== undefined);
    const initialChart = validCharts[0];
    const compareChart = validCharts[1];

      const syncXAxisAndNavigator = () => {
        const initialChartXAxis = initialChart.xAxis[0];
        const compareChartXAxis = compareChart.xAxis[0];

        const minX = initialChartXAxis.min;
        const maxX = initialChartXAxis.max;

        compareChartXAxis.setExtremes(minX, maxX);
        }
     if (initialChart && compareChart) {
      syncXAxisAndNavigator();
     }
  }, [draggableLinePosition]);

  const updateNavigatorExtremes = (chart, position) => {
    const range = 8 * 24 * 3600 * 1000;
    const min = position;
    const max = position + range;
    if (chart) {
      chart.xAxis[0].setExtremes(min, max);
    }
  };

  useEffect(() => {
    if (draggableLinePosition && endDatePrediction) {
      const newDisabledButtons = {};
  
      if (linePositions.length > 0) {
        timeIntervals.forEach((hours) => {
          newDisabledButtons[hours] = true;
        });
      } else {
        timeIntervals.forEach((hours) => {
          const hoursInMs = hours * 3600 * 1000;
          const isDisabled = draggableLinePosition + hoursInMs > endDatePrediction;
          newDisabledButtons[hours] = isDisabled;
        });
      }
  
      setDisabledButtons(newDisabledButtons);
    }
  }, [draggableLinePosition, endDatePrediction, linePositions]);
  

  useEffect(() => {
    if (draggableLinePosition !== null && chartOptions && moveNavigator) {
      const validCharts = HighStock.charts?.filter(chart => chart !== undefined);
      const chart = validCharts[0];
      if (chart) {
        updateNavigatorExtremes(chart, draggableLinePosition);
      }
      setMoveNavigator(false);
    }
  }, [moveNavigator]);
  
  useEffect(() => {
    if (draggableLinePosition !== null && chartOptions && !newForecastLineTimestamp) {
      setChartOptions((prevOptions) => updatePlotLine(prevOptions, draggableLinePosition, purple));
    }
    if (draggableLinePosition !== null && compareChartOptions && showCompareStation && !newForecastLineTimestamp) {
      setCompareChartOptions((prevOptions) => updatePlotLine(prevOptions, draggableLinePosition, purple));
    }
  }, [draggableLinePosition]);

  useEffect(() => {
    if (data) {
      initializeChart(data, setDraggableLinePosition, setChartOptions, hasForecast, forecastData, isCompareStation);
    }
    if (compareData && showCompareStation && selectedCompareStation){
      initializeChart(compareData, setDraggableLinePosition, setCompareChartOptions, hasForecast, compareForecastData, isCompareStation);
    }
  }, [data, compareData, isCompareStation, showCompareStation, selectedCompareStation]);
 
  useEffect(() => {
    if (draggableLinePosition !== null) {
      const formattedDate = dayjs(draggableLinePosition).format('YYYY-MM-DD HH:mm');
      const forecastName = `T0 (${formattedDate}) `;
      fetchPredictionsJSON(draggableLinePosition, setChartOptions, forecastName, null, forecastData);
    }
  }, [draggableLinePosition]);

  useEffect(() => {
    if (draggableLinePosition !== null && compareChartOptions && compareForecastData) {
      const formattedDate = dayjs(draggableLinePosition).format('YYYY-MM-DD HH:mm');
      const forecastName = `T0 (${formattedDate}) `;
      fetchPredictionsJSON(draggableLinePosition, setCompareChartOptions, forecastName, null, compareForecastData);
    }
  }, [draggableLinePosition, compareForecastData, scenarioBase]);
 
  useEffect(() => {
    if (newForecastLineTimestamp !== null) {
      setLinePositions((prevPositions) => {
        if (!prevPositions.includes(newForecastLineTimestamp)) {
          return [...prevPositions, newForecastLineTimestamp];
        }
        return prevPositions;
      });
      setIsAddNewForecast(false);
    }
  }, [newForecastLineTimestamp, isAddNewForecast]);
 
  useEffect(() => {
    Array.from(new Set(linePositions)).forEach((linePosition) => {
      setChartOptions((prevOptions) => {
        const plotLineExists = prevOptions?.xAxis.plotLines?.some(
          (plotLine) => plotLine.value === linePosition.date
        );
  
        if (!plotLineExists) {
          const { date, color, hour } = linePosition;
          const formattedDate = dayjs(date).format('YYYY-MM-DD HH:mm');

          let predefinedForecastName;
          if (hour != null) {
            predefinedForecastName = `T-${hour}h (${formattedDate}) `;
          } else {          
            const deltaHeures = Math.round((draggableLinePosition - date) / (1000 * 60 * 60));
            predefinedForecastName = `T-${deltaHeures}h(${formattedDate})`;
          }
  
          fetchPredictionsJSON(date, setChartOptions, predefinedForecastName, color, forecastData);
  
          return updatePlotLine(prevOptions, date, color);
        }
        return prevOptions;
      });
    });
  }, [linePositions, draggableLinePosition, scenarioBase]);

  useEffect(() => {
      if (compareChartOptions && showCompareStation){
        
        Array.from(new Set(linePositions)).forEach((linePosition) => {
          setCompareChartOptions((prevOptions) => {
            const plotLineExists = prevOptions?.xAxis.plotLines?.some(
              (plotLine) => plotLine.value === linePosition.date
            );
      
            if (!plotLineExists) {
              const { date, color, hour } = linePosition;
              const formattedDate = dayjs(date).format('YYYY-MM-DD HH:mm');
      
              let predefinedForecastName;
              if (hour != null) {
                predefinedForecastName = `T-${hour}h (${formattedDate}) `;
              } else {
                const deltaHeures = Math.round((draggableLinePosition - date) / (1000 * 60 * 60));
                predefinedForecastName = `T-${deltaHeures}h(${formattedDate})`;
              }
      
              fetchPredictionsJSON(date, setCompareChartOptions, predefinedForecastName, color, compareForecastData);
      
              return updatePlotLine(prevOptions, date, color);
            }
            return prevOptions;
          });
        });
      }
  }, [linePositions, draggableLinePosition, compareForecastData, showCompareStation]);

  const handleDeleteComparechart = () => {
    setCompareChartOptions(null);
    setCompareData([]);
    setShowCompareStation(false);
    setSelectedCompareStation(null);
  }

  return (
    <section className="layout-content">
      <div className="left-column common-columns">
       {selectedStation &&  <InfosStation selectedStation={selectedStation} showCompareStation={showCompareStation} setShowCompareStation={setShowCompareStation} compareChartOptions={compareChartOptions} selectedCompareStation={selectedCompareStation} handleDeleteComparechart={handleDeleteComparechart} />}
        {!showCompareStation && <article className="chart-card">
          <p>Prédiction des crues par IA ALCOM-Technologies</p>
          <p>
            <MdCopyright />
            <span>2024 - Tous droits réservés</span>
          </p>
        </article>}

      </div>
      <section ref={ref54} className={showCompareStation ? "middle-column compare-row" : "right-column common-columns"}>
        <div className={showCompareStation ? "charts-container compare-style" : "charts-conatainer"}>
        { !loadingMainChart && chartOptions && chartOptions.series?.length > 0 ?
          <HighchartsReact
              highcharts={HighStock}
              options={chartOptions}
              constructorType="stockChart"
            />
          : <Spin className='loading' />
        }
        {showCompareStation && (
          <>
          { !loadingCompareChart && !!compareChartOptions && compareChartOptions.series?.length > 0 ?
          <HighchartsReact
                highcharts={HighStock}
                options={compareChartOptions}
                constructorType="stockChart"
              /> 
            : <Spin className='loading' />
          }
          </>
        )}
        {!showCompareStation && <Tags
          ref44={ref44}
          setNewForecastLineTimestamp={setNewForecastLineTimestamp}
          chartOptionsForecasts={chartOptionsForecasts}
          setChartOptions={setChartOptions}
          setCompareChartOptions={setCompareChartOptions}
          compareChartOptions={compareChartOptions}
          linePositions={linePositions}
          setLinePositions={setLinePositions}
        />}
        </div>
        <div className={showCompareStation ? "last-column controler column" : "controler"}>
          <article className="chart-card">
            <PredefinedButtons
              chartOptions={chartOptions}
              setCompareChartOptions={setCompareChartOptions}
              setChartOptions={setChartOptions}
              setLinePositions={setLinePositions}
              linePositions={linePositions}
              startDatePrediction={startDatePrediction}
              draggableLinePosition={draggableLinePosition}
              setNewForecastLineTimestamp={setNewForecastLineTimestamp}
              setIsAddNewForecast={setIsAddNewForecast}
              setHasForecast={setHasForecast}
              setOpen={setOpen}
              setSteps={setSteps}
              compareChartOptions={compareChartOptions}
              ref44={ref44}
              ref45={ref45}
            />
          </article>
          {showCompareStation && (
            <Tags
            ref44={ref44}
            setNewForecastLineTimestamp={setNewForecastLineTimestamp}
            chartOptionsForecasts={chartOptionsForecasts}
            setChartOptions={setChartOptions}
            setCompareChartOptions={setCompareChartOptions}
            compareChartOptions={compareChartOptions}
            linePositions={linePositions}
            setLinePositions={setLinePositions}
          />
          )}
          <Timecontroler
            startDatePrediction={startDatePrediction}
            endDatePrediction={endDatePrediction}
            draggableLinePosition={draggableLinePosition}
            setMoveNavigator={setMoveNavigator}
            scenarioBase={scenarioBase}
            setDraggableLinePosition={setDraggableLinePosition}
            chartOptions={chartOptions}
            setNewForecastLineTimestamp={setNewForecastLineTimestamp} 
            linePositions={linePositions} 
            setIsAddNewForecast={setIsAddNewForecast}
            setOpen={setOpen}
            setSteps={setSteps}
            ref54={ref54}
            disabledButtons={disabledButtons}
            moveNavigator={moveNavigator}
          />
        </div>
      </section>
    </section>
  );
};
 
export default StationChart;
 
 